import {z} from "zod";
import {accountIdsResponse} from "./accounts/ids";
import {accountStatusResponse} from "./accounts/status";

export enum Gender {
    MALE = "male",
    FEMALE = "female",
    DIVERSE = "diverse",
}

export const gender = z.nativeEnum(Gender);

export const accountResponse = z.object({
    id: z.string().uuid(),
    ids: accountIdsResponse,
    gender: gender.optional(),
    title: z.string().optional(),
    firstname: z.string().optional(),
    lastname: z.string().optional(),
    street: z.string().optional(),
    street_no: z.string().optional(),
    supplement: z.string().optional(),
    postcode: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
    email: z.string().email().optional(),
    phone: z.string().optional(),
    status: accountStatusResponse,
    profession: z.object({
        id: z.string().uuid(),
        active: z.boolean(),
        alias: z.string(),
        name: z.string(),
        name_de: z.string(),
    }).optional(),
    discipline: z.object({
        id: z.string().uuid(),
        active: z.boolean(),
        alias: z.string(),
        name: z.string(),
        name_de: z.string(),
    }).optional(),
    institution: z.object({
        id: z.string().uuid(),
        active: z.boolean(),
        alias: z.string(),
        name: z.string(),
        name_de: z.string(),
    }).optional(),
    subject: z.object({
        id: z.string().uuid(),
        active: z.boolean(),
        alias: z.string(),
        name: z.string(),
        name_de: z.string(),
    }).optional(),
    updated_at: z.date().optional(),
    created_at: z.date(),
});

export type AccountResponse = z.infer<typeof accountResponse>;

export const accountByIdRequest = z.object({
    id: z.string().uuid(),
});

export type AccountByIdRequest = z.infer<typeof accountByIdRequest>;

export const accountByEmailRequest = z.object({
    email: z.string().trim().email().toLowerCase()
})

export type AccountByEmailRequest = z.infer<typeof accountByEmailRequest>;

export const accountFetchPaginatedRequest = z.object({
    rangeStart: z.number(),
    rangeEnd: z.number(),
});

export type AccountFetchPaginatedRequest = z.infer<typeof accountFetchPaginatedRequest>;

export const accountUpdateRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        gender: gender.optional(),
        title: z.string().optional(),
        firstname: z.string().optional(),
        lastname: z.string().optional(),
        street: z.string().optional(),
        street_no: z.string().optional(),
        supplement: z.string().optional(),
        postcode: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
        email: z.string().email().toLowerCase().optional(),
        phone: z.string().optional(),
        profession_id: z.string().uuid().optional(),
        discipline_id: z.string().uuid().optional(),
        institution_id: z.string().uuid().optional(),
        subject_id: z.string().uuid().optional(),
    })
});

export type AccountUpdateRequest = z.infer<typeof accountUpdateRequest>;

export const accountUpdateSignupGeneralRequest= z.object({
    id: z.string().uuid(),
    data: z.object({
        gender: gender.optional(),
        firstname: z.string().optional(),
        lastname: z.string().optional(),
        country: z.string().optional(),
        email: z.string().email().toLowerCase().optional(),
    })
});

export type AccountUpdateSignupGeneralRequest = z.infer<typeof accountUpdateSignupGeneralRequest>;

export const accountUpdatePersonalDetailsRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        gender: gender.optional(),
        title: z.string().optional(),
        firstname: z.string().optional(),
        lastname: z.string().optional(),
        email: z.string().email().toLowerCase().optional(),
        phone: z.string().optional(),
    })
});

export type AccountUpdatePersonalDetailsRequest = z.infer<typeof accountUpdatePersonalDetailsRequest>;

export const accountUpdateBusinessDetailsRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        street: z.string().optional(),
        street_no: z.string().optional(),
        supplement: z.string().optional(),
        postcode: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
    })
});

export type AccountUpdateBusinessDetailsRequest = z.infer<typeof accountUpdateBusinessDetailsRequest>;

export const accountUpdateProfessionRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        profession_id: z.string().uuid(),
    })
});

export type AccountUpdateProfessionRequest = z.infer<typeof accountUpdateProfessionRequest>;

export const accountUpdateDisciplineRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        discipline_id: z.string().uuid(),
    })
});

export type AccountUpdateDisciplineRequest = z.infer<typeof accountUpdateDisciplineRequest>;

export const accountUpdateInstitutionRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        institution_id: z.string().uuid(),
    })
});

export type AccountUpdateInstitutionRequest = z.infer<typeof accountUpdateInstitutionRequest>;

export const accountUpdateSubjectRequest = z.object({
    id: z.string().uuid(),
    data: z.object({
        subject_id: z.string().uuid(),
    })
});

export type AccountUpdateSubjectRequest = z.infer<typeof accountUpdateSubjectRequest>;

export const accountDeleteRequest = z.object({
    id: z.string().uuid(),
    provider_apple_authorization_code: z.string().optional(),
});

export type AccountDeleteRequest = z.infer<typeof accountDeleteRequest>;
